import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Box, Button, Menu, MenuItem, Popper, Paper, ClickAwayListener } from "@mui/material";
import { useNavigate } from "react-router-dom";

/*
 * Navbar tools from the Direction C design — frontend only, no new endpoints.
 *   <PlanBadge plan="FREE" />
 *   <LeadSearch orgId projectId token />   // search leads by name / phone (Ctrl/⌘+K)
 *   <NewMenu groups={[{ title, items: [{ label, path }] }]} />
 */

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";

const PLAN_TONE = {
  FREE: { bg: "#F1F4F8", edge: "#E4E9EF", ink: "#4A5A6E" },
  BASIC: { bg: "#EAF2FB", edge: "#C9DEF3", ink: "#1B4F82" },
  PRO: { bg: "#EFEDFB", edge: "#D3D0F2", ink: "#4A3FBF" },
};

export const PlanBadge = ({ plan }) => {
  if (!plan) return null;
  const t = PLAN_TONE[String(plan).toUpperCase()] || PLAN_TONE.FREE;
  return (
    <span
      style={{
        flex: "0 0 auto",
        padding: "1px 7px",
        borderRadius: 999,
        background: t.bg,
        border: `1px solid ${t.edge}`,
        color: t.ink,
        fontFamily: FONT,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
      }}
    >
      {plan}
    </span>
  );
};

/* ---------------- Lead search ---------------- */

export const LeadSearch = ({ orgId, projectId, token, width = 360 }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [pool, setPool] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Load once when the user first focuses search — the same lead lists the app
  // already fetches (today's leads + the selected project's leads).
  const load = async () => {
    if (loaded || !orgId || !token) return;
    setLoaded(true);
    const headers = { Authorization: `Bearer ${token}` };
    const base = process.env.REACT_APP_BACKEND_URL;
    const reqs = [axios.get(`${base}/lead/today?orgId=${orgId}`, { headers })];
    if (projectId) reqs.push(axios.get(`${base}/lead/all?projectId=${projectId}&orgId=${orgId}`, { headers }));
    const res = await Promise.allSettled(reqs);
    const seen = new Map();
    res.forEach((r) => {
      if (r.status === "fulfilled" && Array.isArray(r.value.data)) {
        r.value.data.forEach((l) => l?.leadId && seen.set(l.leadId, l));
      }
    });
    setPool([...seen.values()]);
  };

  // Reload when the project changes.
  useEffect(() => {
    setLoaded(false);
    setPool([]);
  }, [projectId, orgId]);

  // Ctrl/⌘+K focuses search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const digits = term.replace(/\D/g, "");
    return pool
      .filter(
        (l) =>
          l.name?.toLowerCase().includes(term) ||
          (digits.length >= 3 && String(l.phone || "").replace(/\D/g, "").includes(digits)),
      )
      .slice(0, 8);
  }, [q, pool]);

  const go = (lead) => {
    setOpen(false);
    setQ("");
    navigate(`/dashboard/lead/${lead.leadId}`);
  };

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: "relative", flex: `0 1 ${width}px`, minWidth: 160 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: 36,
            px: 1.25,
            background: "#F7F9FC",
            border: `1px solid ${open ? PRIMARY : LINE}`,
            borderRadius: "10px",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8A97A8" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            placeholder="Search leads by name or phone"
            onFocus={() => {
              load();
              setOpen(true);
            }}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter" && results[active]) {
                go(results[active]);
              } else if (e.key === "Escape") {
                setOpen(false);
                inputRef.current?.blur();
              }
            }}
            style={{ flex: 1, minWidth: 0, height: "100%", margin: 0, padding: 0, border: 0, outline: "none", background: "transparent", fontFamily: FONT, fontSize: 13.5, color: INK }}
          />
          <span style={{ flex: "0 0 auto", padding: "1px 6px", border: `1px solid ${LINE}`, borderRadius: 6, background: "#FFFFFF", fontFamily: MONO, fontSize: 10.5, color: MUTED }}>
            {isMac ? "⌘K" : "Ctrl K"}
          </span>
        </Box>

        <Popper open={open && q.trim().length > 0} anchorEl={anchorRef.current} placement="bottom-start" style={{ zIndex: 1400, width: anchorRef.current?.offsetWidth }}>
          <Paper sx={{ mt: 0.75, p: 0.5, borderRadius: "12px", border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.28)" }}>
            {!loaded || (loaded && pool.length === 0 && results.length === 0) ? (
              <Box sx={{ px: 1.5, py: 1.25, fontFamily: FONT, fontSize: 13, color: MUTED }}>
                {pool.length === 0 ? "Searching today's and this project's leads…" : "No matching leads"}
              </Box>
            ) : results.length === 0 ? (
              <Box sx={{ px: 1.5, py: 1.25, fontFamily: FONT, fontSize: 13, color: MUTED }}>No matching leads</Box>
            ) : (
              results.map((l, i) => (
                <Box
                  key={l.leadId}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(l)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.25,
                    py: 1,
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: i === active ? "#F4F7FA" : "transparent",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {l.name || "Unnamed lead"}
                    </Box>
                    <Box sx={{ fontFamily: FONT, fontSize: 12, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {l.projectName || "—"}
                    </Box>
                  </Box>
                  {l.status && (
                    <span style={{ flex: "0 0 auto", padding: "2px 8px", borderRadius: 999, background: "#EAF2FB", border: "1px solid #C9DEF3", color: "#1B4F82", fontFamily: FONT, fontSize: 11.5, fontWeight: 600 }}>
                      {l.status}
                    </span>
                  )}
                </Box>
              ))
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

/* ---------------- New ▾ menu ---------------- */

export const NewMenu = ({ groups = [] }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const visible = groups.filter((g) => g.items?.length);
  if (!visible.length) return null;

  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          flex: "0 0 auto",
          height: 36,
          px: 1.75,
          gap: 0.75,
          borderRadius: "10px",
          background: PRIMARY,
          color: "#FFFFFF",
          fontFamily: FONT,
          fontSize: 13.5,
          fontWeight: 700,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { background: "#084767", boxShadow: "none" },
        }}
      >
        New
        <svg width="10" height="10" viewBox="0 0 10 6" aria-hidden="true" style={{ opacity: 0.8 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" />
        </svg>
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 220,
              borderRadius: "12px",
              border: `1px solid ${LINE}`,
              boxShadow: "0 16px 36px -12px rgba(15,27,42,0.28)",
              "& .MuiList-root": { p: 0.5 },
            },
          },
        }}
      >
        {visible.map((g, gi) => [
          <Box
            key={`h-${g.title}`}
            sx={{ px: 1.25, pt: gi === 0 ? 0.75 : 1.25, pb: 0.5, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: "#8A97A8" }}
          >
            {g.title}
          </Box>,
          ...g.items.map((it) => (
            <MenuItem
              key={it.path}
              onClick={() => {
                setAnchor(null);
                navigate(it.path);
              }}
              sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 500, color: INK, borderRadius: "8px", minHeight: 36 }}
            >
              {it.label}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
};
